import React, { memo, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Redirect, useLocation } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LOANS_ROUTING_PARAMS, LoansPaths } from 'app/configs/routesConfig/loans';
import { PENDING_CHANGES_SUBTAB } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/PendingChangesDrawer/PendingChangesDrawer.constants';
import DisclosureDelivery, { DisclosureDeliveryHost } from 'app/shared/modules/DisclosureDelivery';
import LoanApplicationDocuments from 'app/shared/modules/LoanApplicationDocuments';
import withLoading, { WithLoadingPropTypes } from 'hocs/withLoading';
import { compose } from 'redux';

import { DocumentsSubtabRouteParam, LoanApplicationTabRouteParam } from 'constants/loanApplication';
import { PAGE_CODES } from 'constants/pageCodes';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import useLoanId from 'hooks/useLoanId';
import { buildDocumentLoanParams } from 'utils/documentPanes';

import { selectIsClaimAllowed } from 'store/auth/selectors';
import { getDocumentsAction } from 'store/documents/actions';
import { getFormsInfoAction, getLoanSpecificForms } from 'store/forms/actions';
import { getApplicationBorrowersAction } from 'store/loanApplications/actions';
import { selectFilteredDocumentsSubtabs } from 'store/loanApplications/selectors';
import {
  getDocumentCategoriesAction,
  getDocumentFieldTypesAction,
  getDocumentTypesAction,
} from 'store/system/actions';

import { getSharedAccordionStyles } from '../../LoanApplication.sharedStyles';

import LoanCicHistory from './LoanCicHistory';
import { rootStyles } from './LoanDocumentsTab.styles';
import LoanFormGenerationForm from './LoanFormGenerationForm';
import LoanSpecificDocuments from './LoanSpecificDocuments';

const LoanDocumentsTab = ({ setIsLoading, isLoading }) => {
  const theme = useTheme();
  const sharedClasses = useMemo(() => getSharedAccordionStyles(theme), [theme]);

  const dispatch = useDispatch();
  const loanId = useLoanId();

  const { state: navigationState } = useLocation();
  const { applicationId, subtab } = useParams();

  const isCopyDocumentsAllowed = useSelector(
    selectIsClaimAllowed(PAGE_CODES.COPY_DOCUMENTS_TO_ANOTHER_LOAN),
  );

  const filteredSubtabs = useSelector(selectFilteredDocumentsSubtabs);

  const loanParams = useMemo(
    () => buildDocumentLoanParams({ loanId, applicationId }),
    [loanId, applicationId],
  );

  useEffectSafeStrictMode(() => {
    if (!loanParams.loanIdOrLegacyLoanNumber) {
      return;
    }

    setIsLoading(true);

    Promise.allSettled([
      dispatch(getDocumentsAction(loanParams)),
      dispatch(getLoanSpecificForms({ applicationId, loanId })),
      dispatch(getDocumentFieldTypesAction()),
      dispatch(getDocumentTypesAction()),
      dispatch(getDocumentCategoriesAction()),
      dispatch(getFormsInfoAction()),
      dispatch(getApplicationBorrowersAction({ applicationId })),
    ])
      .then(() => setIsLoading(false))
      .catch(Error);
  }, [dispatch, loanParams, setIsLoading, loanId, applicationId]);

  const renderLoanDocumentsTab = () => {
    switch (subtab) {
      case DocumentsSubtabRouteParam.CIC_HISTORY:
        return (
          <div className="px-16 md:px-32">
            <LoanCicHistory />
          </div>
        );

      case DocumentsSubtabRouteParam.DOCUMENTS:
        return (
          <LoanApplicationDocuments
            isExtractedFieldsViewNeeded
            isCopyDocumentsAllowed={isCopyDocumentsAllowed}
            isDataLoaded={!isLoading}
            loanParams={loanParams}
          />
        );

      case DocumentsSubtabRouteParam.DISCLOSURES:
        return (
          <div className="px-16 md:px-32">
            <DisclosureDelivery host={DisclosureDeliveryHost.DOCUMENTS_SUBTAB} />
          </div>
        );

      case DocumentsSubtabRouteParam.FORM_GENERATION:
        return (
          <div className="px-16 md:px-32">
            <Typography className="mb-12 mt-24" variant="bold16">
              Form Generation
            </Typography>

            <Box sx={sharedClasses.accordion}>
              <LoanFormGenerationForm />
            </Box>
          </div>
        );

      case DocumentsSubtabRouteParam.LOAN_SPECIFIC:
        return (
          <div className="px-16 md:px-32">
            <LoanSpecificDocuments />
          </div>
        );

      default:
        return null;
    }
  };

  const isPendingChangesDrawerOpen = subtab === PENDING_CHANGES_SUBTAB;
  const isRedirectNeeded = !isPendingChangesDrawerOpen && !filteredSubtabs.includes(subtab);

  if (isRedirectNeeded) {
    const applicationPathname = LoansPaths.applicationManagement.generatePath({
      [LOANS_ROUTING_PARAMS.applicationId]: applicationId,
      [LOANS_ROUTING_PARAMS.tab]: LoanApplicationTabRouteParam.DOCUMENTS,
      [LOANS_ROUTING_PARAMS.subtab]: DocumentsSubtabRouteParam.DOCUMENTS,
    });

    // eslint-disable-next-line react-perf/jsx-no-new-object-as-prop
    const to = {
      pathname: applicationPathname,
      state: navigationState,
    };

    return <Redirect to={to} />;
  }

  return <Box sx={rootStyles}>{renderLoanDocumentsTab()}</Box>;
};

LoanDocumentsTab.propTypes = {
  ...WithLoadingPropTypes,
};

export default compose(withLoading, memo)(LoanDocumentsTab);
